/**
 * EdgeMeasurements Component
 * Display edge lengths on polygon edges
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 448-503, 56 lines)
 *
 * Features:
 * - Calculate edge length using latitude/longitude distance formula
 * - Display measurements at edge midpoints
 * - Styled labels with white background and border
 * - Show lengths in meters with 2 decimal precision
 */

import React from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';

export interface EdgeMeasurementsProps {
  /** Polygon coordinates [lat, lng][] - must include closing point */
  coordinates: [number, number][];

  /** Whether to show edge measurements */
  show: boolean;
}

/**
 * Display edge length measurements on polygon edges
 *
 * Calculates distance between each pair of vertices and displays
 * a label at the midpoint of each edge.
 */
export function EdgeMeasurements({
  coordinates,
  show,
}: EdgeMeasurementsProps) {
  if (!show || coordinates.length < 2) return null;

  const calculateEdgeLength = (start: [number, number], end: [number, number]): number => {
    const [lat1, lng1] = start;
    const [lat2, lng2] = end;
    const dLat = lat2 - lat1;
    const dLng = lng2 - lng1;
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = 111320 * Math.cos(lat1 * Math.PI / 180);
    const dy = dLat * metersPerDegreeLat;
    const dx = dLng * metersPerDegreeLng;
    return Math.sqrt(dx * dx + dy * dy);
  };

  return (
    <>
      {coordinates.slice(0, -1).map((coord, index) => {
        const nextCoord = coordinates[index + 1];
        const midLat = (coord[0] + nextCoord[0]) / 2;
        const midLng = (coord[1] + nextCoord[1]) / 2;
        const length = calculateEdgeLength(coord, nextCoord);

        return (
          <Marker
            key={`edge-measurement-${index}`}
            position={[midLat, midLng]}
            // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
            icon={L.divIcon({
              className: 'edge-measurement-label',
              html: `<div style="
                background: rgba(255, 255, 255, 0.95);
                padding: 2px 6px;
                border-radius: 4px;
                border: 1px solid #333;
                font-size: 11px;
                font-weight: bold;
                color: #333;
                white-space: nowrap;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              ">${length.toFixed(2)}m</div>`,
              iconSize: [60, 20],
              iconAnchor: [30, 10],
            })}
          />
        );
      })}
    </>
  );
}
